import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", inquiry_type: "retailer", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("Message sent. We'll be in touch soon.");
      setForm(initial);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const types = [
    { v: "retailer", l: "Retailer / Wholesale" },
    { v: "customer", l: "Customer" },
    { v: "other", l: "Other" },
  ];

  return (
    <section id="contact" data-testid="contact" className="py-24 sm:py-32 bg-surface border-y border-surface-border">
      <div className="mx-auto max-w-3xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-12"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-blaze mb-4">Get In Touch</p>
          <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
            Talk to <span className="text-fire">us</span>
          </h2>
          <p className="text-muted-foreground mt-5 text-base sm:text-lg">
            Retailers looking to stock Hot Packz Grabba, or customers with a
            question, drop us a line.
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="space-y-6"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Name *">
              <input data-testid="contact-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
            </Field>
            <Field label="Email *">
              <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Phone">
              <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(555) 000-0000" />
            </Field>
            <Field label="I am a…">
              <select data-testid="contact-type" value={form.inquiry_type} onChange={set("inquiry_type")} className={`${inputCls} appearance-none cursor-pointer`}>
                {types.map((t) => (
                  <option key={t.v} value={t.v} className="bg-ink">{t.l}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Message *">
            <textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={5} className={`${inputCls} resize-none`} placeholder="Tell us what you need…" />
          </Field>

          <button
            data-testid="contact-submit"
            type="submit"
            disabled={sending}
            className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-blaze text-white font-heading uppercase text-sm tracking-wider py-4 px-10 hover:bg-gold hover:text-ink transition-colors duration-200 disabled:opacity-60"
          >
            {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} className="transition-transform duration-200 group-hover:translate-x-1" />}
            {sending ? "Sending…" : "Send Message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-black border border-surface-border text-white px-4 py-3.5 text-sm focus:outline-none focus:border-blaze focus:ring-1 focus:ring-blaze transition-colors duration-200 placeholder:text-muted-foreground/50";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2 block">{label}</span>
      {children}
    </label>
  );
}
